import React from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import moment from 'moment';
import { Card } from 'components/UI';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

// Styled components (Emotion)
const DetailsListItem = styled(Box)({
  borderBottom: '1px solid #EEEEEE',
});

const DetailsListText = styled(Typography)({
  fontSize: 13,
});

const DetailsListSecondText = styled(Typography)({
  fontSize: 13,
  textAlign: 'right',
});

const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));

const TagsText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.85),
}));

function DocumentDetailsWidget({ document, tags, onTagClick }) {
  const { localeFormat } = useSelector((state) => state.organizations);
  return (
    <Card
      headerActionType="vertical"
      data-cy="documents-details-card"
      title={i18n.t('documents.document')}
    >
      <Box>
        {document?.created_time && (
          <DetailsListItem
            pb={1}
            pt={1}
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <DetailsListText variant="subtitle2">
              {i18n.t('documents.creation_date')}:
            </DetailsListText>
            <DetailsListSecondText variant="body2">
              {moment(document.created_time).format(localeFormat)}
            </DetailsListSecondText>
          </DetailsListItem>
        )}
        {document.default?.expiration_time && (
          <DetailsListItem
            pb={1}
            pt={1}
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <DetailsListText variant="subtitle2">
              {`${i18n.t('documents.expiration_date')}:`}
            </DetailsListText>
            <DetailsListSecondText variant="body2">
              {moment(document.default.expiration_time).format(localeFormat)}
            </DetailsListSecondText>
          </DetailsListItem>
        )}
        <DetailsListItem
          pb={1}
          pt={1}
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <DetailsListText variant="subtitle2">{`${i18n.t('common.name')}:`}</DetailsListText>
          <DetailsListSecondText variant="body2">
            {document?.document_type?.name || '-'}
          </DetailsListSecondText>
        </DetailsListItem>
        {document.description && (
          <DetailsListItem
            pb={1}
            pt={1}
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            alignItems="center"
            textAlign="left"
          >
            <DetailsListText variant="subtitle2" sx={{ width: '100%' }}>
              {`${i18n.t('common.description')}:`}
            </DetailsListText>
            <DetailsListText variant="body2" sx={{ marginTop: 1, width: '100%' }}>
              {document.description}
            </DetailsListText>
          </DetailsListItem>
        )}
        {tags.length > 0 && (
          <Box mt={2} pb={1} pt={1} display="flex" flexDirection="row" alignItems="flex-start">
            <TagsText variant="subtitle2">{`${i18n.t('documents.tags')}:`}</TagsText>
            <ChipContainer ml={0.5}>
              {tags.map(({ label }) => (
                <Chip
                  key={label}
                  label={label}
                  clickable
                  color="primary"
                  onClick={() => onTagClick(label)}
                />
              ))}
            </ChipContainer>
          </Box>
        )}
      </Box>
    </Card>
  );
}

DocumentDetailsWidget.propTypes = {
  /**
   * Document to be showed
   */
  document: PropTypes.any,
  tags: PropTypes.array,
  onTagClick: PropTypes.func,
};

DocumentDetailsWidget.defaultProps = {
  tags: [],
  document: {},
  onTagClick: () => {},
};

export default DocumentDetailsWidget;
